/**
 * /tracking/ios-universal-links — operator route for feature #178.
 *
 * Declares a TanStack Router route and renders `IosUniversalLinksPage`.
 * Root wire-up mounts this from `router.tsx` after the prod-main merge.
 *
 * `?domain=` deep-links the page scoped to a single tracking domain, so
 * ops people can jump here from a tracking-domain detail view.
 */

import { createRoute, Link } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { IosUniversalLinksPage } from "@/features/ios-universal-links/IosUniversalLinksPage";

interface TrackingIosUniversalLinksSearch {
	domain?: string;
}

export const trackingIosUniversalLinksRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/tracking/ios-universal-links",
	validateSearch: (
		raw: Record<string, unknown>,
	): TrackingIosUniversalLinksSearch => {
		const v = raw.domain;
		if (typeof v === "string" && v.trim().length > 0) {
			return { domain: v.trim() };
		}
		return {};
	},
	component: TrackingIosUniversalLinksRoute,
});

export default trackingIosUniversalLinksRoute;

export function TrackingIosUniversalLinksRoute() {
	const { domain } = trackingIosUniversalLinksRoute.useSearch();

	return (
		<div className="flex flex-col">
			<nav
				aria-label="Breadcrumb"
				className="mx-auto w-full max-w-6xl px-6 pt-10 text-sm text-zinc-500 sm:pt-16"
			>
				<ol className="flex flex-wrap items-center gap-1">
					<li>
						<Link
							to="/"
							className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
						>
							Dashboard
						</Link>
					</li>
					<li aria-hidden="true" className="text-zinc-300">
						/
					</li>
					<li>
						<span className="rounded-[6px] px-1 py-0.5">Tracking</span>
					</li>
					<li aria-hidden="true" className="text-zinc-300">
						/
					</li>
					<li className="text-zinc-900">iOS Universal Links</li>
				</ol>
			</nav>

			<IosUniversalLinksPage initialDomain={domain} />
		</div>
	);
}

// Named export for the root wire-up cycle.
export { TrackingIosUniversalLinksRoute as TrackingIosUniversalLinksPage };
